/**
 * English — the source of truth for every translatable string on the marketing
 * site.
 *
 * `hi.ts` is typed as `typeof en`, so a key added here without a Hindi
 * counterpart is a build error rather than a silent English leak on the Hindi
 * site. Add here first, then translate.
 *
 * HEADINGS carry two bits of inline markup, rendered by `RichText`:
 *
 *   \n          a line break (usually `hidden sm:block`, so it only splits on
 *               wide screens and the phone gets a natural wrap)
 *   <em>…</em>  the accent run — the coloured half of a two-tone heading
 *
 * Both exist so word ORDER can move between languages. Hindi puts the verb
 * last, so a heading split into fixed "before"/"after" props would force
 * English syntax onto the Hindi copy. Marking the accent inline lets each
 * language put it wherever its own grammar wants it.
 */
const en = {
  common: {
    skipToContent: "Skip to content",
  },

  language: {
    /* aria-label on the switcher. The option labels themselves are NOT here —
       see the note on LANGUAGES in lib/i18n/index.ts. */
    change: "Change language",
  },

  nav: {
    features: "Features",
    pricing: "Pricing",
    contact: "Contact",
    blog: "Blog",
    support: "Support",
    login: "Login",
    freeTrial: "Free Trial",
    startFreeTrial: "Start Free Trial",
    toggleMenu: "Toggle menu",
    home: "Kaympus Home",
  },

  hero: {
    titleLine1: "Smarter Schools.",
    titleLine2: "Stronger Connections.",
    titleAccent: "Better Future.",
    subtitle:
      "A complete school management system and ERP software to run academics, operations, and communication seamlessly.",
    ctaDemo: "Get a Free Demo",
    ctaWatch: "Watch Overview",
    dashboardAlt:
      "Kaympus admin dashboard showing student, teacher, staff and fee collection totals",
    phoneAlt:
      "Kaympus student mobile app home screen showing today's attendance and classes",
    modalTitle: "Kaympus School ERP: Platform Overview",
    modalClose: "Close video",
    modalHeading: "Overview video is on its way",
    modalBody:
      "We’re producing a full platform walkthrough. Until then, see Kaympus live in a personalised demo.",
    modalCta: "Book a Live Demo",
  },

  problem: {
    heading: "Running a School\nShouldn't Feel Like <em>Firefighting.</em>",
    items: [
      "Parents calling for fee receipts continuously.",
      "Teachers juggling manual attendance sheets.",
      "Admins stuck handling scattered Excel files.",
      "Exam season report generation chaos.",
      "Admission enquiries lost in paper registers.",
    ],
    cardTitleLine1: "The Kaympus",
    cardTitleLine2: "Success Standard",
    cardBody:
      "Most schools have patchwork software. Kaympus replaces chaos with complete digital clarity.",
    shiftLabel: "What actually changes",
    shiftBody:
      "Most schools arrive with fees in Excel, attendance on paper, and every reminder typed out by hand in a WhatsApp group. Once admissions, attendance and fees run off one record, receipts are numbered as they're issued and fee reminders go out before the due date, not after it.",
    proofFeatures: "Features live",
    proofModules: "Modules",
    proofDashboards: "Role dashboards",
  },

  whyFail: {
    heading: "Why Most School ERPs <em>Fail Schools</em>",
    bodyLead:
      "Most School ERP Software doesn't fail because of features. It fails because of execution.",
    bodyStrong: "Many schools return to Excel within 6 months.",
    bodyTail: "That's the hard truth we're solving.",
    failTitle: "What Usually Happens",
    failSubtitle: "The Failure Cycle",
    winTitle: "Why Kaympus is Different",
    winSubtitle: "The Kaympus Standard",
    schoolAlt: "School Illustration",
    fails: [
      "Too complicated for teachers",
      "No proper onboarding support",
      "Hidden charges after signup",
      "Slow performance during exam time",
      "Weak support once payment is done",
      "Not built for Indian school realities",
      "Fee payment integrations that break",
    ],
    benefits: [
      "Simple interface designed for non-technical users",
      "Structured onboarding & guided setup",
      "Transparent pricing no surprise add-ons",
      "Built to handle exam & fee peak loads",
      "Dedicated support for growing schools",
      "Designed for Indian academic structures",
      "Reliable multi-gateway payment integration",
    ],
  },

  showcase: {
    heading: "Explore the Kaympus <em>Standout Features</em>",
    body:
      "The capabilities most school ERPs leave out: biometric payroll, admissions, website CMS, digital consent and multi-school control, all in one system.",
    exploreAll: "Explore All Features",
    /* Three fields, and `imageAlt` is the one that carries weight. The panel is
       five real product captures and nothing else — the window chrome, the two
       stat cards and the footer bar that used to wrap them are gone — so the
       alt text is now the only description of each screen that exists, for a
       screen reader and for an image crawler alike. Write what is actually on
       the screen, not "dashboard screenshot". See FEATURE_SHOTS in
       ProductShowcase.tsx; re-shoot a capture and its alt has to be re-read off
       the new one, in both languages. */
    features: [
      {
        title: "Attendance to Payroll",
        subtitle: "Punches flow straight through to payroll",
        imageAlt:
          "Kaympus Run Payroll screen: gross, deductions and net pay worked out for 17 staff from their own attendance, with three unapproved absences flagged before the run can be finalised.",
      },
      {
        title: "Enquiry to Admission",
        subtitle: "From first enquiry to enrolled student profile",
        imageAlt:
          "Kaympus Admission Applications screen: enquiries submitted on the school's own website, each carrying a parent contact and a status of new, reviewed, contacted or accepted.",
      },
      {
        title: "Website & Custom Domain",
        subtitle: "Run your website and domain from inside Kaympus",
        imageAlt:
          "Kaympus Website Content screen: the school's public site edited from inside the ERP — name, tagline, logo, meta title and description, across eighteen page sections.",
      },
      {
        title: "Digital Consent Forms",
        subtitle: "Create, send and track parental consent",
        imageAlt:
          "Kaympus Consent Forms screen: five forms out to one class — annual day, media release, health check-up, sports day and a field trip — each showing how many parents have signed and how many are still pending.",
      },
      {
        title: "Multi-School & Role Control",
        subtitle: "Dedicated dashboards for every role and campus",
        imageAlt:
          "Kaympus Permissions screen: read, write, update and delete granted per person and per section, with department and designation grants layering on top rather than overriding.",
      },
    ],
  },

  roi: {
    heading: "Calculate How Much <em>Kaympus Saves Your School</em>",
    body:
      "Move the sliders to match your school size and watch the numbers update: fee recovery, staff hours and paper costs, worked out for a full academic year.",
    panelTitle: "Your school, roughly",
    reset: "Reset",
    closestMatch: "Closest match",
    custom: "Custom",
    presetSmall: "Small",
    presetGrowing: "Growing",
    presetLarge: "Large",
    presetGroup: "Group",
    sliderStudents: "Total Enrolled Students",
    sliderMonthlyFee: "Average Monthly Fee / Student",
    sliderStaff: "Teaching & Admin Staff",
    unitStudents: "students",
    unitStaff: "staff",
    unitPerMonth: "/mo",
    ariaPerMonth: "{value} per month",
    /* Indian rupee scale words. Digits and the lakh/crore grouping itself do
       not change between languages — only these two labels. */
    crore: "Cr",
    lakh: "Lakhs",
    disclaimer:
      "Estimates based on averages from 500+ Indian partner schools. Your onboarding team will work out your school’s real numbers.",
    gainsLabel: "What your school gains in a year",
    moneyBack: "Money back",
    moneyBackNote: "About {value} a month",
    staffTime: "Staff time saved",
    staffTimeNote: "About {value} hrs / staff",
    hoursSuffix: "hrs",
    whereFrom: "Where the money comes from",
    rowFeesLabel: "Fees collected on time",
    rowFeesNote:
      "Built-in fee tracking & automated online payment links close pending dues.",
    rowPaperLabel: "Paper & printing saved",
    rowPaperNote: "Report cards, circulars & receipts go digital.",
    perStudentBefore: "That’s about ",
    perStudentAfter: " for every student on your roll.",
    ctaNote: "Want the real numbers for your school?",
    ctaButton: "Get my school’s exact numbers",
  },

  multiSchool: {
    heading: "Built for\n<em>Single & Multi-Branch</em>\nSchools",
    body:
      "Kaympus uses secure multi-tenant architecture, meaning every school gets its own <em>dedicated database</em> and isolated digital environment.",
    diagramAria: "Multi-tenant architecture benefits",
    benefits: [
      { title: "Complete Data Security", desc: "No data overlap between institutions." },
      { title: "Separate Login Portals", desc: "Every school gets its own branded gateway." },
      { title: "Dedicated Databases", desc: "The most secure cloud architecture." },
      { title: "Franchise Ready", desc: "Perfect for education groups & networks." },
      { title: "Independent Gateways", desc: "Separate payment config per school." },
      { title: "Custom Subdomains", desc: "A Kaympus subdomain for every branch." },
    ],
  },

  audience: {
    heading: "Who Is\n<em>Kaympus</em> For?",
    body:
      "Whether you manage 200 students or 2,000, the system adapts to your size. We specifically focus on growing schools in Tier 2 & 3 cities.",
    checklist: [
      "Budget Friendly",
      "Needs No Setup",
      "Locked In Cost",
      "Mobile Friendly",
    ],
    cards: [
      {
        title: "Private Schools",
        desc: "Transform traditional schools with integrated digital transformation.",
      },
      {
        title: "CBSE / ICSE",
        desc: "Curriculum-specific tools for academic excellence and growth.",
      },
      {
        title: "K-12 & Play Schools",
        desc: "End-to-end workflows from admissions to section transfers & digital TCs.",
      },
      {
        title: "Tier 2 & 3 Cities",
        desc: "Affordable technology designed for emerging markets.",
      },
    ],
  },

  howItWorks: {
    heading: "Up & running in <em>3 simple steps</em>",
    body:
      "No IT team needed. Our guided setup gets your entire school live in 3 simple steps.",
    stepLabel: "STEP",
    steps: [
      {
        title: "Sign Up & Configure",
        desc: "Create your school, set academic structure, define classes, and invite staff.",
        items: [
          "School profile setup",
          "Basic layout & details",
          "Invite admins & coordinators",
          "Set academic year",
        ],
      },
      {
        title: "Invite Your Team",
        desc: "Onboard teachers, administrators, and students. Each gets a personalized portal.",
        items: [
          "Role-based user accounts",
          "Import & assign user data",
          "Staff assignments",
          "Permission management",
        ],
      },
      {
        title: "Manage & Automate",
        desc: "Run your school from a single dashboard. Automate attendance, fees, and reports.",
        items: [
          "Live attendance tracking",
          "Automated fee collection",
          "Instant result publishing",
          "Smart analytics",
        ],
      },
    ],
  },

  faq: {
    heading: "Got Questions?\nWe've got <em>answers.</em>",
    stillNeedHelp: "Still need help?",
    contactSupport: "Contact our support team",
    categories: {
      general: {
        label: "General",
        items: [
          {
            question: "What is Kaympus?",
            answer:
              "Kaympus is a complete school management platform. Admissions, student and staff records, attendance, timetable, fees, examinations and report cards, library, hostel, transport, HR and payroll, and school-wide communication all run from a single login.",
          },
          {
            question: "Who can use Kaympus?",
            answer:
              "Kaympus is built for K-12 schools, CBSE/ICSE institutions, junior colleges, and multi-branch school groups. It ships with separate logins for school admins, teachers, students, and non-teaching staff, with staff roles created from your own designations. Parents stay connected through their child's login and the mobile app.",
          },
          {
            question: "Is my school data secure?",
            answer:
              "Yes. Every school runs on its own isolated workspace with its own domain, and all traffic is served over encrypted HTTPS. Access is controlled by granular role and permission settings, logins are verified with an OTP, and sensitive actions are recorded in an activity log you can audit any time.",
          },
          {
            question: "Can Kaympus integrate with other tools?",
            answer:
              "Yes. Connect your own payment gateway (Razorpay, PayU, PhonePe, Paytm, Cashfree, Stripe or PayPal), plus a DLT-registered SMS gateway for OTPs and alerts, and your own SMTP email account. Staff attendance also supports biometric and face enrolment devices.",
          },
          {
            question: "How long does implementation take?",
            answer:
              "Most schools are live in a few days. Our team sets up your classes, fee heads and grading scheme, imports your existing student and staff data, and trains your team before you go live. The exact timeline depends on how much historical data you bring across.",
          },
        ],
      },
      features: {
        label: "Features",
        items: [
          {
            question: "How does online fee collection work?",
            answer:
              "You define fee heads and structures per class, and parents pay online from the portal or the mobile app using your connected gateway via UPI, cards, or net banking. Every payment gets a numbered digital receipt you can print or share, and fee follow-ups are handled automatically via in-app notifications and portal alerts.",
          },
          {
            question: "Can we generate board-compliant report cards?",
            answer:
              "Yes. The report card engine uses your own grading scheme and supports CBSE-style strict pass rules, where failing theory fails the subject. Cards carry your school's board and affiliation number along with your logo, principal signature, and seal.",
          },
          {
            question: "What does the attendance module cover?",
            answer:
              "Teachers mark daily class attendance, while staff attendance supports biometric and face enrolment, geofenced check-in, and a punch review screen for corrections. Class, student, and staff attendance reports can be exported whenever you need them.",
          },
        ],
      },
      security: {
        label: "Security",
        items: [
          {
            question: "Where is our school data hosted?",
            answer:
              "Kaympus is fully cloud-hosted and served over encrypted HTTPS, with each school kept in its own isolated workspace so no two schools ever share data. Backups run on a regular schedule.",
          },
          {
            question: "Who owns our school data and can we export it?",
            answer:
              "You maintain 100% ownership of your school data. Student records, fee ledgers, attendance registers, and report cards can be exported to Excel or PDF at any time.",
          },
        ],
      },
      implementation: {
        label: "Implementation",
        items: [
          {
            question: "Is data migration from Excel or a previous ERP free?",
            answer:
              "Yes, assisted migration is included. Our team imports, cleans, and structures your existing Excel or CSV records at no additional cost.",
          },
          {
            question: "Do you provide staff & teacher training?",
            answer:
              "Yes. We run interactive online training sessions for teachers and admins, backed by written user guides and video walkthroughs your team can revisit any time.",
          },
        ],
      },
      support: {
        label: "Support",
        items: [
          {
            question: "What support channels do you offer?",
            answer:
              "You get direct phone and email support for urgent queries, plus a helpdesk built right into Kaympus where your staff can raise a ticket and track it to resolution.",
          },
          {
            question: "Do parents and students need to install an app?",
            answer:
              "The Kaympus mobile app gives students and parents fees, attendance, results, homework, and school notices on their phone. Everything is also available in the browser, so nothing is locked behind the app.",
          },
        ],
      },
    },
  },

  /**
   * The /features page.
   *
   * `groups` and `narratives` are keyed by the ids in featureData.ts — NOT
   * ordered lists — because the rail filters, sorts and splits them by role,
   * and the footer links to six of them by id. A positional array would tie
   * the translation to the order the catalogue happens to render today.
   *
   * The fake UI inside the device frames (featureMocks.ts) is deliberately
   * NOT here. Those panels sit next to real product screenshots, which are
   * English PNGs — translating the mock half would make the pair disagree
   * about what the software looks like.
   */
  features: {
    hero: {
      /* Animated word by word, so it's a list rather than a sentence. Keep the
         count at four — FeaturesHero staggers them against a fixed delay. */
      headlineLead: ["Run", "Your", "Entire", "School"],
      headlineAccent: "from One Screen.",
      subtitle:
        "Explore our complete ERP suite, from admissions and attendance to fee collection, payroll, library, transport, and live parent communication.",
      ctaDemo: "Get a Free Demo",
      ctaWatch: "Watch Overview",
      cardTitle: "Kaympus ERP: Feature Suite",
      /* Twelve tiles, index-aligned with the colour list in FeaturesHero. */
      cardTiles: [
        "Student & Staff",
        "Attendance",
        "Fee Collection",
        "Examinations",
        "Communication",
        "Analytics",
        "Sports Mgmt",
        "Library",
        "Transport GPS",
        "HR & Payroll",
        "Hostel Mgmt",
        "Leave Mgmt",
      ],
      cardCount: "{count} features",
      cardAllIncluded: "All Included",
      modalTitle: "Kaympus School ERP: Feature Overview",
      modalVideoTitle: "Kaympus ERP Overview Video",
      modalClose: "Close video",
    },

    catalogue: {
      heading: "Every feature your school\nneeds. <em>Nothing it doesn't.</em>",
      intro:
        "{features} features across {categories} categories in one connected system, not a bundle of add-ons. Below are the highlights for each of the four people who use it: what they get, what changes for your school, and how it differs from the way other school ERPs are built. The full walkthrough is the demo.",
      workspaceTitle: "{role} workspace",
      whatYouGet: "What you get",
      howItHelps: "How it helps your school",
      differentHeading: "How this is different from other school ERPs",
      usualApproach: "The usual approach",
      withKaympus: "With Kaympus",
      ctaTitle: "See all {count} running on your own data.",
      ctaBody: "A 30-minute walkthrough using your real classes, fees and staff. No slides.",
      ctaButton: "Book a live demo",
    },

    roles: {
      admin: { label: "Admin", blurb: "For the principal and the office" },
      teacher: { label: "Teacher", blurb: "For the classroom, not the back office" },
      staff: { label: "Staff", blurb: "For accounts, library, hostel and the front desk" },
      student: { label: "Student & Parent", blurb: "For the family, on the phone" },
    },

    /* Titles and taglines for all seventeen groups. The footer's six rows read
       from this same map, so a group can't be called one thing in the rail and
       another in the footer. */
    groups: {
      "admin-overview": { title: "School at a Glance", tagline: "Live numbers, exceptions first" },
      "admin-people": { title: "Students & Staff Records", tagline: "One record per person" },
      "admin-academics": { title: "Academics & Examinations", tagline: "Timetable to report card" },
      "admin-finance": { title: "Fees, Payroll & Compliance", tagline: "Money in, money out" },
      "admin-operations": { title: "Campus & Communication", tagline: "Facilities, notices, website" },
      "teacher-day": { title: "My Teaching Day", tagline: "Today's periods and attendance" },
      "teacher-classroom": { title: "Classwork & Assessment", tagline: "Homework, tests and results" },
      "teacher-app": { title: "Teacher Mobile App", tagline: "The classroom, in your pocket" },
      "teacher-self": { title: "Leave, Salary & Reports", tagline: "Your own leave and payslips" },
      "staff-workspace": { title: "Your Own Login", tagline: "Scoped to your job" },
      "staff-app": { title: "Staff Mobile App", tagline: "Punch in, apply, download" },
      "staff-frontdesk": { title: "Front Office & Fee Counter", tagline: "The counter and the queue" },
      "staff-facilities": { title: "Library, Hostel & Facilities", tagline: "Books, beds and buses" },
      "student-home": { title: "The Family's View", tagline: "One child, one clear view" },
      "student-app": { title: "Parent & Student App", tagline: "Alerts the day they happen" },
      "student-academics": { title: "Classwork, Exams & Results", tagline: "Homework, exams and results" },
      "student-fees": { title: "Fees & Payments", tagline: "Due, paid, and payable online" },
    },

    /* Three lines in `provides`, three in `helps` — see the house rules at the
       top of featureNarratives.ts, which apply to the translation too:
       `helps` states an outcome, and `different.others` must stay a fair
       description of how rival ERPs are genuinely built. */
    narratives: {
      "admin-overview": {
        what: "The screen a principal or administrator lands on. One page that answers the questions you would otherwise walk around the building to ask: how many children are in today, what came in at the fee counter, who has not turned up, and what is waiting on your reply.",
        provides: [
          "A dashboard that reads live from every part of the school, not from last night's job",
          "Today's attendance, today's collection and today's staff presence, side by side",
          "The exceptions surfaced first: absentees, overdue fees, unanswered complaints",
        ],
        helps: [
          "You know yesterday's collection and today's staff attendance before the first cup of tea",
          "The morning starts with the handful of things that are wrong, instead of a hunt for them",
          "A question in a management meeting gets answered during the meeting",
        ],
        different: {
          others: "Most school ERPs open on a wall of static tiles refreshed by a nightly job, so the number on screen is yesterday's, and finding the students behind it means an export to Excel.",
          kaympus: "Every figure is live and clickable through to the records that produced it. Nothing on the dashboard is a dead end.",
        },
      },
      "admin-people": {
        what: "The whole life of a student in your school, held in one place, from the day a stranger first phones to ask about admission to the day you hand over the transfer certificate. Staff records work the same way.",
        provides: [
          "One record per child that everything else points at, so a name is never typed twice",
          "Enquiries and admissions tracked as a pipeline, with nothing sitting in a personal inbox",
          "Teaching and non-teaching staff records feeding attendance, payroll and the timetable",
        ],
        helps: [
          "A student changes section in one place, and the timetable, fee plan, report card and parent app all follow",
          "You stop maintaining the same list in three registers and finding all three disagree",
          "Admission season stops depending on which clerk remembers which enquiry",
        ],
        different: {
          others: "Admissions is commonly a separate module with its own student list, so an admitted child is re-entered by hand, and the enquiry that started it is lost the moment it converts.",
          kaympus: "The enquiry becomes the application, the application becomes the student, and it is the same record throughout. Nothing is retyped and the trail stays intact.",
        },
      },
      "admin-academics": {
        what: "Everything that runs on the timetable: who teaches what, who was present, what was set as homework, what was examined and what goes on the report card at the end of it.",
        provides: [
          "Timetables built against real teacher availability rather than against a blank grid",
          "Daily attendance that reaches the parent the same day it is marked",
          "Report cards printed in your school's format, not in the vendor's",
        ],
        helps: [
          "Parents hear about a run of absences in week two, not in the term-end meeting",
          "Report card season stops being three weeks of collating spreadsheets",
          "A teacher's marks entry is the last time those numbers are handled by a human",
        ],
        different: {
          others: "Attendance, exams and report cards are often three modules that exchange data by export and import, which is why the report card deadline is a crisis in most schools.",
          kaympus: "Marks entered in class are already on the report card. The generation step is a print, not a data-collection exercise.",
        },
      },
      "admin-finance": {
        what: "Money in and money out. Fee plans and collection on one side, staff salary and statutory compliance on the other, both reading the same people records the rest of the school uses.",
        provides: [
          "Fee plans with instalments, sibling and staff concessions, and late fees applied by rule",
          "Online payment by UPI, card and netbanking, reconciled against the same ledger as cash",
          "Monthly payroll with statutory deductions calculated, not typed",
        ],
        helps: [
          "The day's collection total is right at 5pm, not after someone tallies the receipt book",
          "Parents pay at 11pm from a phone, and the office sees it before it opens",
          "Salary day stops being a two-day spreadsheet exercise for one overloaded person",
        ],
        different: {
          others: "Fees and payroll are usually priced and built as separate products, often by different teams, and reconciling online payments against counter collection is a manual monthly job.",
          kaympus: "Both run inside the same system on the same staff and student records, and an online payment lands in the same ledger as a cash receipt the moment it clears.",
        },
      },
      "admin-operations": {
        what: "The parts of a school that are not lessons (the library, the hostel, the buses, the games field), plus every way the school talks to the people in it, including the public website parents judge you by.",
        provides: [
          "Library, hostel, transport and sports running inside the same system as everything else",
          "Messaging between office, teachers and parents that never exposes a personal number",
          "Your public school website and custom domain, managed by the school",
        ],
        helps: [
          "A holiday declared at 8am is on every parent's phone by 8:05, not on a WhatsApp forward",
          "A complaint stops depending on which clerk happened to answer the phone",
          "The website stops being a thing you pay an agency to change twice a year",
        ],
        different: {
          others: "Library, hostel and transport are typically sold as paid add-ons with their own logins and their own copy of the student list, and the school website is an unrelated purchase entirely.",
          kaympus: "They are included and they share the student record, so a hostel resident and a bus passenger are the same child you admitted, and the website is one of the modules.",
        },
      },
      "teacher-day": {
        what: "A teacher's own screen, cut down to the classes they actually teach. It opens on today: which periods, which sections, who is missing.",
        provides: [
          "Today's periods and classes on the screen they land on, with no navigating to reach it",
          "Their own sections and student lists, and nobody else's",
          "Attendance marked for a full class in under a minute",
        ],
        helps: [
          "Attendance gets marked in the classroom, so the register is complete by mid-morning rather than by evening",
          "Teachers stop queuing at the admin desk for a list somebody else has to pull for them",
          "New teachers are productive on day one, because there is nothing to learn but their own screen",
        ],
        different: {
          others: "Many ERPs give a teacher the admin panel with buttons hidden, so they navigate a menu built for a principal to reach the two screens they need.",
          kaympus: "The teacher view is built as its own thing. It shows their classes, their periods and their pending work, and nothing else exists to get lost in.",
        },
      },
      "teacher-classroom": {
        what: "The work a teacher sets and the work they assess (homework, assignments, class tests and exams), from setting it to publishing the result.",
        provides: [
          "Homework and assignments that reach parents the moment they are posted",
          "Class tests and marks entered directly, with no spreadsheet in the middle",
          "Exam schedules and results published to the students who sat them",
        ],
        helps: [
          "\"He said there was no homework\" stops being an argument anyone can have",
          "Marks are entered once, by the person who marked the paper, and never re-keyed",
          "Chasing consent slips before a trip stops being a week of reminders",
        ],
        different: {
          others: "Homework is often a one-way notice board, and marks are collected on a template spreadsheet that the office then re-enters into the exam module.",
          kaympus: "What the teacher posts is what the parent sees, and what the teacher enters is what appears on the report card. There is no re-entry step to lose things in.",
        },
      },
      "teacher-app": {
        what: "A real Android and iOS app for teachers, so the work that happens in the classroom is recorded in the classroom rather than remembered until the staff room.",
        provides: [
          "A native app on both stores, not a shrunk-down copy of the website",
          "Attendance marked at the front of the class, on the phone in their hand",
          "A reply to a parent that does not require handing out a personal number",
        ],
        helps: [
          "Attendance data arrives while it is still accurate, instead of being reconstructed at 4pm",
          "Teachers stop carrying a paper register between rooms",
          "Parent messages get answered the same day, because answering takes thirty seconds",
        ],
        different: {
          others: "The teacher app is frequently an add-on licence, and where it exists it is often a wrapper around the mobile website with no offline tolerance.",
          kaympus: "The app ships with the system at no extra cost and is built for the phone, so marking a class works the same on the field as it does on office wifi.",
        },
      },
      "teacher-self": {
        what: "The employment side of a teacher's account: their leave, their salary, and the reports they are accountable for as a class teacher.",
        provides: [
          "Leave applied for from the app, with the current balance visible before applying",
          "Payslips and salary history they can download themselves",
          "Attendance and performance reports for their own sections",
        ],
        helps: [
          "The HR desk stops fielding \"how many casual leaves do I have left\" twenty times a month",
          "Payslip requests stop arriving as a WhatsApp message to the accountant",
          "A class teacher can answer a parent's question about their child without asking the office first",
        ],
        different: {
          others: "Teacher HR usually lives in whatever payroll software the accounts department bought, which teachers have no login for, so every request goes through a person.",
          kaympus: "Leave, payslips and teaching records sit behind the same login, because they are all facts about the same employee.",
        },
      },
      "staff-workspace": {
        what: "The account a non-teaching employee gets: an accountant, a librarian, a warden, an office clerk. Same system, but showing only the part of the school that is that person's job.",
        provides: [
          "A home screen scoped to the job, not a cut-down copy of the principal's",
          "Only the modules the role has actually been granted, with the rest genuinely absent",
          "Tickets raised and tracked to a close, rather than remembered",
        ],
        helps: [
          "You can give an office clerk a login without giving them the salary register",
          "Staff stop learning a menu that is 80% irrelevant to them",
          "Handover to a new joiner is a permissions grant, not a week of shadowing",
        ],
        different: {
          others: "Non-teaching staff are commonly handed a shared office login, because the permission model only really distinguishes admin from teacher.",
          kaympus: "Permissions are granted per person and per module, so every staff member has their own account and their own accountable trail.",
        },
      },
      "staff-app": {
        what: "The staff phone app: attendance, leave and salary for people who are rarely at a desk.",
        provides: [
          "Biometric punch-in, geofenced so it only counts on campus",
          "Leave applied for and balance checked without visiting HR",
          "This month's payslip downloaded as a PDF",
        ],
        helps: [
          "Attendance is captured for staff who never sit at a computer during their shift",
          "The HR desk stops being interrupted for questions the employee can answer themselves",
          "Payslip distribution stops being a monthly printing job",
        ],
        different: {
          others: "Staff attendance usually means a physical biometric device and a monthly export, with no self-service for the employee at all.",
          kaympus: "The phone is the device. The punch, the leave balance and the payslip are all in the employee's hand, and the register updates as it happens.",
        },
      },
      "staff-frontdesk": {
        what: "The front office: the counter where fees are taken, admission enquiries arrive and parents turn up with questions.",
        provides: [
          "Fee collection with the receipt printed as the money is taken",
          "Cash, cheque and online payments handled at the same counter",
          "Admission enquiries and application intake captured at the desk",
        ],
        helps: [
          "The counter closes with a total that already matches the system",
          "An enquiry taken at the desk on Monday is still being followed up in week three",
          "A parent who was promised a callback actually gets one",
        ],
        different: {
          others: "The fee counter is often a separate accounting package, so the receipt it prints and the student record the school keeps live in two different systems.",
          kaympus: "The receipt is issued against the student's own record, so the parent's balance updates on their app before they have left the building.",
        },
      },
      "staff-facilities": {
        what: "The facilities a school runs alongside teaching (the library, the hostel, the sports programme and the buses), each with the person who runs it able to run it themselves.",
        provides: [
          "A book catalogue with a working issue and return desk",
          "Hostel rooms, bed allotment and occupancy you can see at a glance",
          "Transport routes and stops mapped to the children who use them",
        ],
        helps: [
          "The librarian stops keeping a parallel register to the one the ERP was supposed to replace",
          "A warden can answer \"which bed is free\" without walking the block",
          "Facility staff get a tool for their own job instead of a form to fill in for someone else's",
        ],
        different: {
          others: "These are the classic paid add-ons, each with its own login, its own copy of the student list, and no connection to attendance or fees.",
          kaympus: "They are included and they read the same student record, so a library fine and a hostel bed belong to the same child the school already knows.",
        },
      },
      "student-home": {
        what: "What the family sees. The same school, filtered down to one child, for the student on their own login and for their parents on theirs.",
        provides: [
          "A home screen built for the student, and one for the parents",
          "The attendance record, day by day, not as a term-end percentage",
          "Notices, events and the holiday calendar as the school publishes them",
        ],
        helps: [
          "Parents stop phoning the office to ask about attendance, because they can already see it",
          "A pattern of absences is visible to a parent in week two rather than at the term-end meeting",
          "The school communicates to every family at once without a WhatsApp group to moderate",
        ],
        different: {
          others: "The parent view is commonly the last thing built and the first thing cut: a read-only summary, updated by an overnight sync, sold as an add-on.",
          kaympus: "The family portal ships with the system and reads live. What a parent sees at 9:00 is what the office entered at 8:55, with no sync window to explain away.",
        },
      },
      "student-app": {
        what: "The phone app that most parents will actually use, because for a working parent the app is the school.",
        provides: [
          "An attendance alert on the day it happens, not at the end of term",
          "Homework and test notifications as the teacher posts them",
          "Classroom chat and school circulars in one place",
        ],
        helps: [
          "A parent knows their child missed school today, while today is still useful",
          "Homework arguments end, because the homework is on the parent's phone",
          "Circulars stop being a paper note that never left the school bag",
        ],
        different: {
          others: "The parent app is usually an extra licence per student, and often shows a summary that was accurate this morning.",
          kaympus: "It is included for every family and reads live data. There is no per-parent fee and no overnight refresh.",
        },
      },
      "student-academics": {
        what: "The academic side of the family's account: what has been set, what is coming, and how it went.",
        provides: [
          "Homework and assignments with their due dates, as the teacher posted them",
          "Results the moment the school publishes them",
          "Report cards downloadable as a PDF, not collected on a Saturday",
        ],
        helps: [
          "A parent can help with the homework because they know what it is",
          "Exam week is planned for, instead of discovered",
          "Nobody queues at the office for a duplicate copy of a report card",
        ],
        different: {
          others: "Results usually reach families as a printed sheet or a PDF emailed weeks later, and homework, if it is shared at all, arrives in a class WhatsApp group.",
          kaympus: "It comes from the same record the teacher entered, so the family sees it as soon as the school decides to publish it, in the same place every time.",
        },
      },
      "student-fees": {
        what: "The fee side of the family's account: what is owed, what has been paid, and a way to pay it without coming to the school.",
        provides: [
          "What is due, what is paid and what falls due next, in one view",
          "Payment online by UPI, card or netbanking",
          "A reminder before the due date rather than a demand after it",
        ],
        helps: [
          "Collection improves because paying takes a minute and does not need a school visit",
          "The office stops issuing duplicate receipts for ones that went missing",
          "Fee reminders stop being an awkward phone call to a parent",
        ],
        different: {
          others: "Online fee payment is often bolted on through a payment gateway page that does not know the student, so the office reconciles the transactions by hand each week.",
          kaympus: "The payment is made against the child's own fee plan and posts to the same ledger as a cash receipt, so the balance is correct the moment it clears.",
        },
      },
    },
  },

  cta: {
    headingLine1: "Ready to Simplify Your",
    headingLine2: "School Operations?",
    bodyLine1: "Stop managing systems. Start managing growth.",
    bodyLine2: "Book your Free Demo Today.",
    btnDemo: "Start Free Demo",
    btnSales: "Talk to Sales",
    badgeNoCard: "No credit card required",
    badgeTrial: "Free 15-day access",
    badgeCancel: "Cancel anytime",
    photoAlt: "School Administrator",
  },

  /**
   * /pricing.
   *
   * Plan names, descriptions, key points and excluded-feature lists are NOT
   * here — they come from the subscriptions API, i.e. whatever the school
   * typed into the admin panel. Only the page's own chrome is translatable.
   */
  pricing: {
    /* Three parts rather than one `<em>` string: the accent span also wraps an
       animated underline SVG, so it has to stay a real element in the JSX. */
    headingBefore: "Invest in your school's",
    headingAccent: "future",
    headingAfter: "",
    subtitle:
      "Subscribing to any plan instantly creates your school instance. Every plan includes the full ERP suite, no hidden feature gates.",
    /* Index-aligned with BILLING_CYCLES — the key is sent to the API and never
       translated, only what the tab reads. */
    cycles: [
      { label: "Monthly", suffix: "/month" },
      { label: "3 Months", suffix: "/3 months" },
      { label: "6 Months", suffix: "/6 months" },
      { label: "Yearly", suffix: "/year" },
    ],
    loading: "Loading plans...",
    errorNoPlans: "No plans available",
    errorLoad: "Failed to load pricing",
    viewAllPlans: "View All Plans",
    mostPopular: "Most Popular",
    recommended: "Recommended",
    choosePlan: "Choose Plan",
    whatsIncluded: "What's included",
    save: "SAVE {percent}%",
    trustNoCard: "No credit card required · Claim your free trial",
    customPlanBefore: "Need a custom plan?",
    customPlanLink: "Contact our sales team",
    faqHeading: "Pricing questions, <em>answered.</em>",
    faqLabel: "Billing",
    faqItems: [
      {
        question: "Can I switch plans later?",
        answer: "Absolutely. You can upgrade or downgrade your plan at any time. Prorated charges will be applied automatically to your next billing cycle.",
      },
      {
        question: "Is there a setup fee?",
        answer: "No, there are no hidden setup fees or onboarding costs. You only pay the flat monthly or annual rate.",
      },
      {
        question: "How does data migration work?",
        answer: "Our onboarding team will help you migrate your existing student, staff, and financial records via secure Excel/CSV imports completely free of charge.",
      },
    ],
  },

  /** /plans — the FAQ block. The plan cards themselves come from the API. */
  plans: {
    /* Same three-part shape as `pricing.heading*` and for the same reason —
       the accent word is a gradient span, not plain text. The rest of the card
       chrome (Most Popular, Choose Plan, What's included, SAVE, the billing
       cycle tabs) is shared with /pricing and read from `pricing.*` rather
       than duplicated here. */
    headingBefore: "Invest in your school's",
    headingAccent: "Future",
    headingAfter: "",
    subtitle:
      "Subscribing to a plan automatically creates your school instance. Manage everything from day one with Kaympus.",
    trust: ["99.9% Uptime SLA", "5-Minute Setup", "No Credit Card Required"],
    errorLoad: "Failed to load plans",
    errorConnect: "Failed to connect to server",
    retry: "Retry",
    faqHeading: "Plan questions, <em>answered.</em>",
    faqLabel: "Choosing a plan",
    faqItems: [
      {
        question: "Which plan should we start with?",
        answer: "Pick the plan that covers your current student count and the modules you need on day one. You can move up or down later without losing any data, so most schools start on the plan matching their present size rather than where they expect to be next year.",
      },
      {
        question: "What happens when we outgrow our student limit?",
        answer: "We'll flag it before it becomes a problem and move you to the next tier. Nothing stops working mid-session and no records are locked.",
      },
      {
        question: "Can we try Kaympus before committing?",
        answer: "Yes. Start a free trial with no credit card, load a class or two of real data, and see how it fits your workflow before you pay for anything.",
      },
      {
        question: "Do all plans include support and onboarding?",
        answer: "Yes. Every plan includes assisted data migration, staff training, the in-product helpdesk, and WhatsApp and phone support.",
      },
    ],
  },

  /** /contact — the demo request form. */
  contact: {
    heading: "See Kaympus\n<em>in action</em>",
    subtitle:
      "Get a personalised 30-minute walkthrough tailored to your school's size and requirements. Our specialists will show you exactly how Kaympus can solve your biggest challenges.",
    benefits: [
      "Live walkthrough of all features",
      "Custom setup for your school size",
      "Q&A with a product specialist",
      "No credit card needed",
    ],
    typeDemo: "Free Demo",
    typeEnquiry: "Enquiry",
    successTitle: "Request Received!",
    /* Split around the visitor's own name, which is obviously not translated. */
    successBefore: "Thank you,",
    successAfter:
      "! Our team will reach out to you within 24 hours to schedule your personalized demo.",
    successFallbackName: "there",
    submitAnother: "Submit another request",
    labelName: "Your Name",
    labelSchool: "School Name",
    labelEmail: "Email Address",
    labelPhone: "Phone Number",
    labelMessage: "Your Message",
    labelStudents: "Number of Students",
    placeholderName: "e.g. Rajesh Kumar",
    placeholderSchool: "e.g. DAV Public School",
    placeholderEmail: "you@school.com",
    placeholderPhone: "+91 98765 43210",
    placeholderMessage: "Tell us what you'd like to know about Kaympus...",
    placeholderStudents: "Select range...",
    /* Labels only. The value each maps to is the string the API stores and
       must stay identical in both languages. */
    studentRanges: [
      "Under 200 students",
      "200 - 500 students",
      "500 - 1,000 students",
      "1,000 - 2,000 students",
      "2,000+ students",
    ],
    submitting: "Submitting...",
    submitEnquiry: "Send My Enquiry",
    submitDemo: "Book My Free Demo",
    errorSubmit: "Failed to submit your request. Please try again.",
    errorGeneric: "Something went wrong. Please try again.",
  },

  /** /system-status. Uptime figures and latencies are data, not copy. */
  systemStatus: {
    banner: "All Systems Operational • 99.98% Uptime",
    heading: "Kaympus <em>System Status</em>",
    subtitle:
      "Real-time operational status, service latency metrics, and system maintenance logs.",
    currentHeading: "Current Service Status",
    currentSubtitle: "Live monitoring across cloud nodes",
    liveSync: "Live Auto-Sync",
    latencyLabel: "Response Latency:",
    uptimeLabel: "UPTIME (30 DAYS)",
    statusOperational: "Operational",
    logsHeading: "Recent System Logs & Maintenance",
    /* Names only — index-aligned with serviceMetrics in the page. */
    services: [
      "Core ERP Cloud API & Web Dashboard",
      "SMS & Push Notification Broadcast Gateway",
      "Online Fee Payment Gateway Integration (UPI/Cards)",
      "CBSE Report Card & PDF Generation Engine",
      "GPS Bus Fleet Telematics & Live Location Stream",
      "Biometric & RFID Gate Attendance Sync",
      "Parent & Teacher Android/iOS Mobile Apps API",
      "Automated Off-site Daily Database Backups",
    ],
    incidents: [
      {
        date: "August 2, 2026",
        title: "Scheduled Maintenance: Payment Gateway Optimization",
        status: "Completed",
        desc: "Routine database index optimization for payment webhooks. All services remained fully operational with zero downtime.",
      },
      {
        date: "July 18, 2026",
        title: "SMS & Notification Queue Delivery Optimization",
        status: "Resolved",
        desc: "Brief 6-minute latency spike in SMS broadcast delivery during peak morning hours. Queue automatically resolved by backup node.",
      },
      {
        date: "June 24, 2026",
        title: "Cloud Infrastructure Capacity Upgrade",
        status: "Completed",
        desc: "Expanded server capacity by 300% to handle nationwide CBSE Term 1 report card result publishing smoothly.",
      },
    ],
  },

  /**
   * /case-studies.
   *
   * School names stay as they are — they are the names of real institutions.
   * The attributed quotes ARE translated: a Hindi reader can't use an English
   * testimonial, and a marked translation is the normal way to carry one
   * across. `author` keeps the person's name and translates only their title.
   */
  caseStudies: {
    badge: "Proven Results & Success Stories",
    heading: "How Leading Schools <em>Thrive with Kaympus</em>",
    subtitle:
      "Explore real case studies from 500+ schools that digitized fee collection, automated CBSE report cards, and boosted parent trust.",
    topMetrics: [
      "On-time Fee Collection",
      "Teacher Time Saved / Term",
      "Parent App Read Rate",
      "Schools Transformed",
    ],
    keyImpact: "Key Impact Metrics",
    ctaBefore: "Transform Your School Like",
    studies: [
      {
        location: "Jaipur, Rajasthan",
        students: "3,200 Students",
        badge: "FEE AUTOMATION & UPI",
        title: "How DAV Public School Digitized 100% Fee Collection & Saved 180+ Hours Per Month",
        metricLabels: ["Fee Collection Efficiency", "Counter Queue Time Reduced", "Digital Receipts Delivered"],
        summary: "Prior to Kaympus, parents waited in hour-long queues during fee weeks. By introducing automated UPI payment links and instant digital receipts, 98% of parents now pay fees online within 48 hours.",
        quote: "Kaympus completely transformed our administrative ops. Parents love the convenience of paying online via UPI, and our accounts team reconciled fees in real-time.",
        author: "Dr. Rajesh Sharma, Principal",
      },
      {
        location: "Ajmer, Rajasthan",
        students: "2,800 Students",
        badge: "CBSE MARKSHEETS",
        title: "Automating CBSE Report Cards & Exam Grading for 2,800+ Students Instantly",
        metricLabels: ["Teacher Hours Saved / Term", "Marksheet Accuracy", "Report Printing Speed"],
        summary: "Compiling Term 1 & Term 2 exam marks previously took teachers 2 weeks of manual Excel entries. Kaympus auto-calculated grades, attendance, and co-scholastic rubrics to generate instant printable PDFs.",
        quote: "Our teachers used to spend weekends filling report cards manually. With Kaympus, the entire term results were published and delivered to parents in 24 hours.",
        author: "Sr. Teresa D'Souza, Vice Principal",
      },
      {
        location: "Jodhpur, Rajasthan",
        students: "2,100 Students",
        badge: "SMS & APP SYNC",
        title: "Achieving 99% Parent Engagement via Real-time SMS & Mobile App Alerts",
        metricLabels: ["Daily Attendance Alerts", "Circular Read Rate", "SMS Cost Reduced"],
        summary: "Traditional SMS notifications were frequently unread or blocked by DND filters. Switching to Kaympus app push notifications enabled instant rich-media updates, homework PDFs, and fee reminders.",
        quote: "Parents now receive daily attendance updates and fee receipts straight in the Kaympus app. Communication gaps have dropped to zero.",
        author: "Vikram Rathore, Director",
      },
      {
        location: "Udaipur, Rajasthan",
        students: "1,950 Students",
        badge: "GPS FLEET & RFID GATE",
        title: "Ensuring 100% Student Safety with Real-time GPS Bus Tracking & Gate RFID Scanners",
        metricLabels: ["Gate RFID Scan Rate", "Parent GPS App Usage", "Bus Route Efficiency"],
        summary: "Parents were constantly calling the school office for bus arrival updates. Kaympus live fleet telematics and RFID gate scanners give parents real-time visibility into their child's safety and ETA.",
        quote: "Parent anxiety regarding school bus location disappeared. Kaympus GPS telematics is the best safety feature we've ever deployed.",
        author: "Meenakshi Verma, Headmistress",
      },
    ],
  },

  /** /video-tutorials. Durations, categories and embed ids stay in the page. */
  videoTutorials: {
    badge: "Video Library & Tutorials",
    heading: "Master Kaympus <em>in Minutes</em>",
    subtitle: "Watch step-by-step video tutorials and masterclass walkthroughs for every Kaympus feature.",
    featuredBadge: "FEATURED MASTERCLASS",
    featuredTitle: "Kaympus School ERP: Complete Operational Walkthrough",
    featuredDesc: "Learn how Kaympus unifies Fee Management, App Notifications, CBSE Marksheets, and Live GPS Fleet Tracking into one easy-to-use platform.",
    playerTitle: "Kaympus ERP Video Masterclass",
    tutorialSuffix: "{duration} tutorial",
    watchFull: "Watch Full Tutorial ({duration})",
    watchNow: "Watch Now",
    categories: [
      "Platform Masterclass",
      "Fee Management",
      "Academics & Exams",
      "Operations & Fleet",
      "Communication",
      "Finance & HR",
    ],
    videos: [
      {
        title: "Kaympus School ERP: 5-Minute Platform Overview",
        desc: "A high-level walkthrough of the Kaympus dashboard, fee automation, student profiles, and app notifications.",
      },
      {
        title: "Setting Up Online Fee Gateways & Automated Digital Receipts",
        desc: "Learn how to configure UPI/Paytm payment links, generate instant receipts, and send auto reminders.",
      },
      {
        title: "CBSE & ICSE Marksheet Generator & Report Card Printing",
        desc: "Step-by-step tutorial on defining grading scales, uploading teacher marks, and printing bulk instant PDFs.",
      },
      {
        title: "Smart Attendance Marking & Live Bus GPS Telematics",
        desc: "How gate RFID scanners and mobile attendance sync in real-time with live bus location tracking for parents.",
      },
      {
        title: "App Notifications, SMS & Message Templates Setup",
        desc: "Guide to setting up notification templates and sending instant broadcasts to parents in the Kaympus app.",
      },
      {
        title: "Staff HR Payroll & Ledger Reconciliation Masterclass",
        desc: "Automating teacher salaries, deductions, leave management, and exporting audit reports for board meetings.",
      },
    ],
  },

  /**
   * /privacy-policy and /terms-and-conditions.
   *
   * These are legal documents. The Hindi text is a good-faith translation for
   * readability — if the two ever disagree, the English is the operative
   * version, which is the normal convention for a translated policy. Keep any
   * future edit to one in the same commit as the other.
   */
  legal: {
    lastUpdated: "Last Updated: March 17, 2026",
    /**
     * The privacy policy linked from the Play Store / App Store listing, and
     * its #delete-account section is the Play "account deletion" URL.
     *
     * Every statement here was checked against the code on 13 Oct 2026 (app
     * manifest + lib/, server models/services). If you change what the app or
     * server collects, shares or retains, change this text in the same commit
     * — Play reviews the policy against the APK's permissions and the Data
     * Safety form. `{email}` and `{phone}` are filled from the SuperAdmin
     * Business Profile contacts at render time.
     */
    privacy: {
      lastUpdated: "Last Updated: October 13, 2026",
      heading: "Our Privacy Policy,\n<em>In Plain Words.</em>",
      subtitle:
        "What the Kaympus website and mobile app collect, why we collect it, who we share it with, how long we keep it, and how you can have it deleted.",
      tocTitle: "On this page",
      contactCard: {
        title: "Questions about your data?",
        body: "Write to us and we will reply within 30 days.",
      },
      closing: {
        heading: "Want to see, correct or <em>delete</em> your data?",
        body: "Ask your school, or write to us at {email}. We answer every privacy request within 30 days.",
        cta: "Contact us about privacy",
      },
      sections: [
        {
          title: "Who We Are",
          tag: "Introduction",
          paragraphs: [
            "Kaympus is a school management platform run by Kayease Global Solutions LLP, Jaipur, Rajasthan, India (“Kaympus”, “we”, “us”). This policy covers the Kaympus website (kaympus.com), each school’s Kaympus portal and school website, and the Kaympus mobile app for Android and iOS.",
            "Schools use Kaympus to run their daily work. Most information in Kaympus — student records, attendance, fees and so on — is entered and controlled by your school. For that information the school decides what is collected and why, and we handle it on the school’s behalf and on its instructions. Under India’s Digital Personal Data Protection Act, 2023, the school is the Data Fiduciary and we are its Data Processor. For information we collect for ourselves, such as demo requests on our website or a school’s subscription billing, we are responsible.",
          ],
          bullets: [],
          after: ["If anything here is unclear, contact us at {email}."],
        },
        {
          title: "Information We Collect",
          tag: "What We Collect",
          paragraphs: [
            "We collect only what the feature you are using needs. Depending on your role (student, parent, teacher, staff or school administrator) and the features your school has switched on, this can include:",
          ],
          bullets: [
            { term: "Account details", desc: "Name, mobile number, email address, role, school code and employee or admission number. You sign in with your mobile number and a one-time password (OTP) sent by SMS." },
            { term: "Student records", desc: "Entered by the school: name, photo, date of birth, gender, class and section, parents’ or guardians’ names and phone numbers, and address. Only if the school records them: Aadhaar number, religion, category, blood group, medical notes and certificates." },
            { term: "Staff records", desc: "Entered by the school: name, photo, designation, department, contact details, documents, attendance, leave and payroll information." },
            { term: "School activity", desc: "Attendance, timetables, homework and the assignments you submit, exam marks and report cards, notices, messages, consent forms you sign (including a drawn signature), and library, hostel, sports and transport records." },
            { term: "Fees and payments", desc: "Fee amounts, due dates, receipts and payment references. Card, UPI and bank details are entered on the payment provider’s own secure screen. Kaympus never sees or stores them." },
            { term: "Face and fingerprint data (staff only)", desc: "Photos and face templates for face-recognition attendance, and fingerprint templates where the school uses a fingerprint scanner. See “Face Recognition” below." },
            { term: "Location", desc: "Your phone’s location when a staff member marks attendance, and the bus’s location while a driver runs a transport trip. See “Location Data” below." },
            { term: "Wi-Fi access point identifier", desc: "When staff mark attendance, the app reads the identifier (BSSID) of the Wi-Fi access point the phone is connected to, so the school can confirm the staff member is on campus. We do not read the network name or anything sent over it." },
            { term: "Device and app information", desc: "Device model, operating system version and app version, sent with attendance punches and problem reports. Our servers record your IP address when you use Kaympus. We do not collect your advertising ID, IMEI, phone serial number or contacts." },
            { term: "Photos and files you upload", desc: "Documents, assignment PDFs, photos of pickup guardians, and screenshots you attach to support requests." },
            { term: "Cards and codes scanned", desc: "When school staff scan a student’s transport card, a gate pass or a library book, we record what was scanned, by whom and when." },
            { term: "Support requests", desc: "Messages and screenshots you send through Help & Support, with basic device information that helps us fix the problem." },
            { term: "Website visitors", desc: "If you request a demo or contact us: the details you enter (name, school, phone, email, message). Our marketing website also uses Google Analytics, described under “Cookies” below." },
          ],
          after: [],
        },
        {
          title: "Face Recognition",
          tag: "Biometrics",
          paragraphs: [
            "Face-recognition attendance is used only for school staff, and only if the school switches it on. It is never used for students or parents.",
          ],
          bullets: [
            { term: "Enrolment", desc: "The staff member takes four photos in the app. The photos are sent to our servers, where a face template (a set of numbers that describes the face) is made. A school administrator must approve the enrolment before it is used. The photos and templates are stored with the staff member’s record." },
            { term: "Marking attendance", desc: "The app first checks on the phone that a real person is in front of the camera, for example by asking you to blink or turn your head. That check runs on the phone and those camera frames are not uploaded. One photo is then sent to our servers to match against your face template, and it is kept with that attendance record as proof of the punch." },
            { term: "Gate kiosks", desc: "Some schools set up a shared phone or tablet at the gate. So that it keeps working without internet, that device stores the face templates of the school’s enrolled staff, and holds punch photos until it can upload them." },
            { term: "Fingerprints", desc: "Where a school uses a desk fingerprint scanner, fingerprint templates are stored with the staff member’s record and used the same way." },
          ],
          after: [
            "We use face and fingerprint data only to record attendance. We never sell it, use it for advertising, or share it with anyone other than your school. To remove your face or fingerprint data, ask your school administrator or write to us at {email}.",
          ],
        },
        {
          title: "Location Data",
          tag: "Location",
          paragraphs: ["We use your phone’s location in only three situations:"],
          bullets: [
            { term: "Staff attendance", desc: "When a staff member taps to mark attendance, the app reads the location once to check they are on the school campus. The location and its accuracy are saved with that attendance record. If the school has not turned on the campus check, location is not needed." },
            { term: "Bus trips (drivers and attendants)", desc: "While a driver or attendant has a bus trip running, the app shares the bus’s location about every 15 seconds so parents and the school can see where the bus is. On Android this keeps working with the screen off and shows a permanent “Bus trip in progress” notification. Tracking stops when the trip is ended or the app is closed. We never track a driver outside a trip." },
            { term: "Placing bus stops", desc: "Transport staff can tap “use my location” once to place a bus stop on the map." },
          ],
          after: [
            "We do not collect location from student or parent accounts. Parents see the bus’s position, never their own. Bus location history is deleted automatically after at most 90 days, or sooner if the school chooses.",
          ],
        },
        {
          title: "App Permissions and Why We Ask",
          tag: "Permissions",
          paragraphs: [
            "The app asks for a permission only when you use a feature that needs it. You can refuse or withdraw any permission in your phone’s settings; only the related feature stops working.",
          ],
          bullets: [
            { term: "Camera", desc: "Face-recognition attendance, scanning QR codes and barcodes (transport, gate passes, library books, kiosk setup), and taking guardian photos for pickup cards." },
            { term: "Location (precise)", desc: "Checking that staff are on campus when they mark attendance, and sharing the bus’s location during a transport trip." },
            { term: "Location in a foreground service", desc: "Used only by drivers and attendants while a bus trip is running, with a visible notification for the whole trip." },
            { term: "Nearby devices (Bluetooth)", desc: "Connecting to the school’s RFID card reader on the bus. The app looks only for that reader and does not use Bluetooth to find your location or other devices." },
            { term: "Wi-Fi state", desc: "Reading the Wi-Fi access point identifier during staff attendance, and checking whether you are online." },
            { term: "Notifications", desc: "Showing messages, notices, fee reminders and alerts from your school." },
            { term: "Files and photos", desc: "Only the files you choose to attach through the system file picker. The app does not scan your storage." },
          ],
          after: ["The app does not access your contacts, call logs, SMS messages or microphone."],
        },
        {
          title: "How We Use Information",
          tag: "Purpose",
          paragraphs: ["We use information to:"],
          bullets: [
            { term: "Run the service", desc: "Sign you in, show you your school’s information, and provide the features your school uses." },
            { term: "Communicate", desc: "Send OTPs, notifications, SMS and emails for your school, such as attendance alerts and fee reminders." },
            { term: "Process payments", desc: "Create and confirm fee and subscription payments through the payment provider you choose." },
            { term: "Keep Kaympus secure", desc: "Detect misuse, investigate problems, and keep an activity log of changes made in each school." },
            { term: "Support you", desc: "Answer your questions and fix problems you report." },
            { term: "Meet legal duties", desc: "Comply with the law, court orders and lawful requests from authorities." },
          ],
          after: [
            "We do not sell personal data. The app shows no ads, and we never use your data, or your child’s data, for advertising or to build marketing profiles.",
          ],
        },
        {
          title: "Who We Share It With",
          tag: "Sharing",
          paragraphs: ["We share information only as described here:"],
          bullets: [
            { term: "Your school", desc: "Authorised people at your school see information according to their role. For example, a class teacher sees their class. Parents see their own child’s information." },
            { term: "SMS delivery", desc: "SMSGatewayHub, to send OTPs and SMS messages (your phone number and the message text)." },
            { term: "Email delivery", desc: "Google Workspace (Gmail), to send emails." },
            { term: "App notifications", desc: "Google Firebase Cloud Messaging, to deliver notifications (a device token and the notification text)." },
            { term: "Payments", desc: "Razorpay, PhonePe, Paytm, PayU or Cashfree, whichever your school uses. They receive the payer’s name, phone, email and the amount, and handle card and UPI details themselves under their own privacy policies." },
            { term: "Maps", desc: "OpenStreetMap, or another map provider chosen by the school, supplies map images for transport maps. Your device requests them directly, so the provider sees your IP address but no Kaympus data." },
            { term: "Fonts", desc: "The app downloads its fonts from Google Fonts, which sees your IP address." },
            { term: "Hosting", desc: "Our servers and file storage run on rented cloud servers, which store data for us." },
            { term: "Law and safety", desc: "Authorities, when the law requires it or to protect someone’s safety." },
            { term: "Business changes", desc: "A buyer or successor if our business is merged or sold, who must keep protecting the data under this policy." },
          ],
          after: ["We give each service provider only what it needs for its task."],
        },
        {
          title: "Children’s Data",
          tag: "Children",
          paragraphs: [
            "Many Kaympus users are students under 18. Student accounts are created by the school, not by the child, and parents usually sign in to their child’s account with their own mobile number.",
            "The school is responsible for getting a parent’s or guardian’s consent before adding a child’s information to Kaympus, as the Digital Personal Data Protection Act, 2023 requires. We use children’s data only to provide the school’s services. We do not show children ads, track them for advertising, or monitor their behaviour beyond the school’s own records, such as attendance.",
          ],
          bullets: [],
          after: ["A parent who wants to see, correct or delete their child’s data can ask the school, or write to us at {email}."],
        },
        {
          title: "How Long We Keep It",
          tag: "Retention",
          paragraphs: ["We keep information only as long as it is needed:"],
          bullets: [
            { term: "School records", desc: "Kept while the school uses Kaympus, unless the school deletes them sooner. The school decides how long to keep student and staff records, and may have to keep some (such as fee receipts and leaving certificates) by law." },
            { term: "When a school leaves", desc: "If a school’s account is deleted, it can be restored for 30 days. After that its records are permanently deleted from our database. The school can download a full copy of its data before then. Uploaded files such as photos and documents are deleted when the school or a user asks us to." },
            { term: "Bus location history", desc: "Deleted automatically after at most 90 days." },
            { term: "One-time passwords", desc: "Expire within minutes and are deleted once used." },
            { term: "Activity logs", desc: "Deleted automatically after 365 days." },
            { term: "Server logs", desc: "Technical logs, which include IP addresses, are rotated and deleted after about 14 days." },
            { term: "Notifications", desc: "In-app notifications are deleted 90 days after you read them." },
            { term: "Demo and contact requests", desc: "Kept while we are in touch with you. You can ask us to delete them at any time." },
          ],
          after: [],
        },
        {
          title: "How We Protect It",
          tag: "Security",
          paragraphs: ["We take reasonable technical and organisational steps to protect your information, including:"],
          bullets: [
            { term: "Encrypted connections", desc: "All traffic between the app or browser and our servers uses HTTPS (TLS)." },
            { term: "Separate school databases", desc: "Each school’s data is kept in its own database, and every request is checked against the user’s school and role." },
            { term: "Secure sign-in", desc: "OTP sign-in with limited attempts and a short expiry. Your sign-in token is kept in your phone’s secure storage (Android Keystore or iOS Keychain)." },
            { term: "Protected credentials", desc: "Payment-gateway keys and other service credentials are stored encrypted." },
            { term: "Activity logging", desc: "Changes made in each school are recorded in an activity log the school can review." },
          ],
          after: [
            "No system is perfectly secure. If a breach affects your personal data, we will tell the affected school, and the Data Protection Board of India where the law requires.",
          ],
        },
        {
          title: "Your Choices and Rights",
          tag: "Your Rights",
          paragraphs: ["Under the Digital Personal Data Protection Act, 2023 you have the right to:"],
          bullets: [
            { term: "Access", desc: "Get a summary of the personal data held about you and how it is used." },
            { term: "Correction", desc: "Have wrong or incomplete data corrected. You can change your email and emergency contact in the app; your school updates other details." },
            { term: "Erasure", desc: "Ask for your data to be deleted when it is no longer needed, except records your school must keep by law." },
            { term: "Withdraw consent", desc: "Withdraw consent you gave, for example by turning off a permission or asking for your face data to be removed." },
            { term: "Grievance", desc: "Complain to us and get a reply. If you are not satisfied, you can complain to the Data Protection Board of India." },
            { term: "Nominate", desc: "Name someone to use these rights for you in case of death or incapacity." },
          ],
          after: [
            "Because your school controls most of your data, asking the school is usually quickest. You can also write to us at {email}. We will check your identity, involve your school where needed, and reply within 30 days.",
          ],
        },
        {
          title: "Deleting Your Account and Data",
          tag: "Deletion",
          paragraphs: [
            "Kaympus accounts are created by your school, so your school can deactivate or delete your account at any time. You can also ask us directly:",
          ],
          bullets: [
            { term: "Step 1", desc: "Email {email} with the subject “Delete my Kaympus account”." },
            { term: "Step 2", desc: "Include your school’s name or school code, your registered mobile number, and whether you are a student/parent, teacher or staff member." },
            { term: "Step 3", desc: "We confirm the request with you and tell your school, delete your account within 30 days, and email you when it is done." },
          ],
          after: [
            "What is deleted: your sign-in account, profile and contact details, notification tokens, the photos and documents you uploaded, and, for staff, face photos, face templates and fingerprint templates.",
            "What may be kept: records your school must keep by law, such as fee receipts, attendance registers and leaving certificates. These are kept only as long as the law requires and are no longer linked to an active account.",
          ],
        },
        {
          title: "Cookies and Browser Storage",
          tag: "Cookies",
          paragraphs: [
            "When you sign in to a Kaympus portal in a web browser, we store a sign-in cookie so you stay signed in, and use your browser’s local storage to remember things like the accounts you switch between. These are needed for the portal to work.",
            "Our marketing website (kaympus.com, not school portals or the app) uses Google Tag Manager and Google Analytics to count visits and see which pages are useful. Google sets its own cookies for this. You can block them in your browser settings or with Google’s Analytics opt-out add-on.",
            "The mobile app does not use cookies, and contains no analytics or advertising SDK.",
          ],
          bullets: [],
          after: [],
        },
        {
          title: "Changes and Contact",
          tag: "Updates",
          paragraphs: [
            "We may update this policy as Kaympus changes. We will change the date at the top of this page, and tell schools about important changes.",
            "Kayease Global Solutions LLP, Jaipur, Rajasthan, India. Privacy and grievance contact: {email}, phone {phone}.",
          ],
          bullets: [],
          after: [],
        },
      ],
    },
    terms: {
      heading: "The Foundation of\n<em>Our Partnership.</em>",
      subtitle:
        "Clear, transparent, and fair legal guidelines designed to protect both the digital integrity of your institution and our commitment to service.",
      sections: [
        {
          title: "Agreement to Terms",
          tag: "Legal",
          content: "By accessing or using Kaympus, you agree to be bound by these Terms and Conditions. These terms constitute a legally binding agreement between your institution and Kaympus regarding your use of our School ERP platform and related services.",
          details: ["Binding Agreement", "Institutional Responsibility", "Service Acceptance", "Legal Compliance"],
        },
        {
          title: "Service Provision",
          tag: "Platform",
          content: "We provide a modular ERP platform designed for educational institutions. While we strive for 99.9% uptime, services are provided as is. We reserve the right to modify, suspend, or discontinue any part of the service with prior notice to active institutional administrators.",
          details: ["Uptime Commitment", "Feature Updates", "Scheduled Maintenance", "Service Availability"],
        },
        {
          title: "Acceptable Use",
          tag: "Conduct",
          content: "Users must use the platform for legitimate educational and administrative purposes only. Any attempt to reverse engineer, scrape data, or bypass security protocols is strictly prohibited and may result in immediate suspension of service and legal action.",
          details: ["Prohibited Activities", "Security Integrity", "Authorized Access", "Data Ethical Use"],
        },
        {
          title: "Limitation of Liability",
          tag: "Governance",
          content: "Kaympus shall not be held liable for any indirect, incidental, or consequential damages arising from the use of our platform. Institutions are responsible for maintaining their own backup of data and ensuring the accuracy of information entered into the system.",
          details: ["Liability Caps", "Data Accuracy", "User Responsibility", "Indemnification"],
        },
      ],
    },
    refund: {
      heading: "Our Cancellation &\n<em>No-Refund Policy.</em>",
      subtitle:
        "Every Kaympus subscription payment is final. Here is exactly how cancellations work, why fees are non-refundable, and what happens if a payment ever goes wrong.",
      sections: [
        {
          title: "Cancelling Your Subscription",
          tag: "Cancellation",
          content: "Your institution can cancel its Kaympus subscription at any time from the subscription settings or by writing to our support team. Cancellation stops the next renewal — your school keeps full access to the platform and its data until the end of the period already paid for. The amount already paid for that period is not refunded, and no cancellation fee is charged.",
          details: ["Cancel Anytime", "Access Till Period End", "Renewal Stops Immediately", "No Cancellation Fee"],
        },
        {
          title: "No Refunds on Subscriptions",
          tag: "Policy",
          content: "All subscription fees are final. Once a Kaympus subscription is purchased and activated, the payment is non-refundable in any circumstance — including unused time in the billing period, partial or no usage of the platform, plan downgrades, or a change of mind. We encourage every school to take a free demo and evaluate Kaympus fully before purchasing.",
          details: ["All Payments Final", "No Pro-Rata Refunds", "No Refund on Downgrades", "Free Demo Before You Buy"],
        },
        {
          title: "Payment Errors",
          tag: "Exceptions",
          content: "The only amounts we reverse are genuine transaction errors: a duplicate charge captured for the same order, or money debited from your account without the corresponding subscription being activated. These are corrections of failed payments — not refunds of a purchased subscription — and are returned to the original payment method within 7–10 business days.",
          details: ["Duplicate Charges Reversed", "Failed Debits Returned", "Original Payment Method", "7–10 Business Days"],
        },
        {
          title: "Raising a Billing Query",
          tag: "Process",
          content: "For any billing question or to report a transaction error, write to our support team with your school code and payment reference, or reach us through the Contact page. Every query is reviewed within 2–3 business days and we keep you informed at each step until it is resolved.",
          details: ["Email or Contact Page", "School Code + Payment Ref", "2–3 Day Review", "Updates Till Resolved"],
        },
      ],
    },
  },

  /**
   * /blog — the index chrome only. Post titles, excerpts and bodies are
   * authored in the CMS and render as written.
   */
  blog: {
    heading: "The Kaympus <em>Blog</em>",
    subtitle: "Guides, product updates and ideas for running a modern school.",
    /* Labels only. The category string each maps to is what the API filters
       on, so it stays identical in both languages — see BLOG_CATEGORIES. */
    categoryTabs: ["All Articles", "School Operations", "Fee Management", "Buying Guide"],
    filterNotice: "Showing guides & articles for category:",
    clearFilter: "Clear filter (Show all) →",
    emptyTitle: "No posts found for this category.",
    emptyBody: "Try exploring other categories or view all articles.",
    viewAll: "View All Articles",
  },

  /**
   * /support.
   *
   * `resources` is keyed by id, matching LINKED_RESOURCES in the page — the
   * search results, the topic filter and the deep links all resolve by id.
   *
   * The `keywords` arrays stay in the page and stay ENGLISH. They are search
   * fodder, not copy: indexing them alongside the translated title and
   * description means a Hindi reader can search in either language and still
   * land on the right answer.
   */
  support: {
    badge: "Kaympus Support & Helpdesk",
    heading: "How can we <em>help you</em> today?",
    subtitle: "Search answers on setup, fee collection, parent notifications, exams and transport, or browse the categories below.",
    searchPlaceholder: "Search articles, guides, fee setup...",
    searchLabel: "Search Kaympus support guides and answers",
    clearSearch: "Clear search",
    popular: ["Fee Setup", "SMS Alerts", "CBSE Marksheet", "GPS Bus Tracking", "Student Import"],
    directLinks: ["All Features", "Blog & Guides", "Plans & Pricing", "Contact Support"],
    browseHeading: "Browse by <em>Category</em>",
    browseSubtitle: "Pick the area you need help with and read the guides and articles on our blog.",
    exploreGuides: "Explore Guides",
    resultsHeading: "Results for",
    resultsCountOne: "{count} resource matched",
    resultsCountOther: "{count} resources matched",
    allCategories: "All categories",
    noResults: "No results for “{query}”",
    noResultsBody: "Try a broader word such as fees, attendance or marksheet, or ask us directly and we’ll answer.",
    srResultOne: "{count} result for {query}",
    srResultOther: "{count} results for {query}",
    faqHeading: "Frequently asked <em>questions.</em>",
    faqLabel: "Common questions",
    stillNeedHelp: "Still need help?",
    stillNeedHelpBody: "Our dedicated school success team is ready to assist you 24/7.",
    kinds: {
      answer: { label: "Answer", cta: "More detail" },
      article: { label: "Article", cta: "Read article" },
      page: { label: "On Kaympus", cta: "Open page" },
    },
    topics: [
      { title: "Getting Started & Setup", desc: "Initial school provisioning, admin profile setup, staff onboarding, and basic configurations." },
      { title: "Fee Collection & Gateways", desc: "Setting up UPI/NetBanking, customized fee structures, automated receipts, and due alerts." },
      { title: "Notifications & Alerts", desc: "DLT SMS integration, automated parent notifications, broadcast alerts, and in-app chat." },
      { title: "CBSE / ICSE Report Cards", desc: "Exam planning, grading scales, multi-term result compilation, and instant PDF generation." },
      { title: "Transport & Live GPS", desc: "Fleet route management, driver assignment, RFID gate check-ins, and parent bus tracking." },
      { title: "Security & Permissions", desc: "Role-based access control, multi-branch admin privileges, data encryption, and audit logs." },
    ],
    faqItems: [
      {
        question: "How fast can our school be onboarded on Kaympus?",
        answer: "Kaympus is cloud-native and pre-configured for modern schools. Our team imports your students, staff, and fee structures, sets up classes and grading, and most schools are live within a few days.",
      },
      {
        question: "How does Kaympus reach parents?",
        answer: "Notifications go out over a DLT-registered SMS gateway, email through your own SMTP account, and push notifications in the Kaympus mobile app. Teachers and admins can also message parents directly from the built-in chat.",
      },
      {
        question: "Can parents pay school fees online using UPI or Net Banking?",
        answer: "Absolutely. Connect your own gateway (Razorpay, PayU, PhonePe, Paytm, Cashfree, Stripe or PayPal) and parents can pay by UPI, net banking, or card from the portal or mobile app. Every payment gets a numbered receipt against the student's fee ledger.",
      },
      {
        question: "Does Kaympus support CBSE grading and marksheets?",
        answer: "Yes. The report card engine uses your own grading scheme and supports CBSE-style strict pass rules, where failing theory fails the subject. Cards carry your school's board and affiliation number along with your logo, principal signature, and seal.",
      },
      {
        question: "Can parents track the school bus live?",
        answer: "Yes. Once a route is mapped to a vehicle and driver, parents see the bus moving on a live map in the Kaympus app and get an alert as it approaches their stop. Gate check-ins are recorded against the student's attendance record.",
      },
      {
        question: "Is our school data secure and compliant?",
        answer: "Every school runs in its own isolated workspace served over encrypted HTTPS, with granular role and permission controls, OTP-verified logins, scheduled backups, and an activity log of sensitive actions that you can audit at any time.",
      },
    ],
    resources: {
      "migration": {
        title: "Importing students and staff from Excel or a previous ERP",
        desc: "Assisted migration is included at no extra cost. Send us your existing Excel or CSV records and our team imports, cleans, and structures them for you: students, staff, and fee structures alike.",
      },
      "staff-roles": {
        title: "Staff roles, permissions and granular access",
        desc: "Roles are built from your own staff designations, and each role controls which modules and records that member of staff can open. Logins are OTP-verified, and school admins, teachers, students, and non-teaching staff each get their own separate login.",
      },
      "fee-structures": {
        title: "Setting up fee heads, structures and installments",
        desc: "Fee heads and structures are defined per class, and each can be split into installments with its own due dates. Concessions apply per student, and once a structure is live, fee follow-ups run automatically through in-app notifications and portal alerts.",
      },
      "receipts": {
        title: "Receipts, dues ledgers and fee exports",
        desc: "Every payment produces a numbered digital receipt recorded against the student's fee ledger, which you can print or share. Fee ledgers and outstanding-dues reports export to Excel or PDF whenever you need them for a management review.",
      },
      "fee-article": {
        title: "Why fee collection is always 'almost on target', and never actually on target",
        desc: "Where the last 10-15% of fee collection quietly leaks away each term, and what to change about the follow-up.",
      },
      "broadcasts": {
        title: "Fee reminders, circulars and broadcast alerts",
        desc: "Fee follow-ups go out automatically as in-app notifications and portal alerts once a due date passes. Admins and teachers can also broadcast circulars and notices to one class or the whole school, and message parents directly from the built-in chat.",
      },
      "whatsapp-article": {
        title: "Your school's WhatsApp groups are running the office. Here's what that actually costs.",
        desc: "What breaks when fee follow-ups, leave requests, and notices all live in unmanaged WhatsApp groups.",
      },
      "marks-entry": {
        title: "Exam planning, marks entry and result compilation",
        desc: "Schedule exams, open marks entry for teachers, and compile results across multiple terms. The report card engine then applies your own grading scheme, including CBSE-style strict pass rules where failing theory fails the subject.",
      },
      "attendance": {
        title: "Attendance: daily class marking, biometric and geofenced",
        desc: "Teachers mark daily class attendance from the portal or the app. Staff attendance additionally supports biometric and face enrolment, geofenced check-in, and a punch review screen for corrections. Class, student, and staff attendance reports export whenever you need them.",
      },
      "routes": {
        title: "Routes, stops, vehicles and driver assignment",
        desc: "Map each route with its stops, assign a vehicle and a driver to it, and attach students to the stop they board from. Transport fees link back to the student's fee structure like any other fee head.",
      },
      "audit-log": {
        title: "Audit log and activity history",
        desc: "Sensitive actions are recorded in an activity log you can audit at any time: who changed what, and when, across fees, marks, and student records.",
      },
      "data-ownership": {
        title: "Who owns your school data, and how to export it",
        desc: "You keep 100% ownership of your school data. Student records, fee ledgers, attendance registers, and report cards export to Excel or PDF at any time, and backups run on a regular schedule.",
      },
      "status": {
        title: "Live platform status & uptime",
        desc: "Current availability of the Kaympus platform, mobile apps, payments, and notification delivery.",
      },
      "features": {
        title: "The complete Kaympus feature list",
        desc: "Every module in one catalogue: admissions, attendance, timetable, fees, exams, library, hostel, transport, HR and payroll.",
      },
      "plans": {
        title: "Plans, pricing and subscription billing",
        desc: "What each plan includes, how pricing works, and how renewals and invoices are handled.",
      },
      "buying-article": {
        title: "Buying a school ERP in a Tier 2 or Tier 3 city? Ask these 6 questions before you sign.",
        desc: "The six questions worth asking any school ERP vendor before you commit, and what the answers tell you.",
      },
      "contact": {
        title: "Talk to the school success team",
        desc: "Book a walkthrough or raise anything the answers here don't cover, by phone, email, or a scheduled call.",
      },
    },
  },

  blogStrip: {
    heading: "From the Blog",
    body: "Guides, product updates and ideas for running a modern school.",
    viewAll: "View all posts →",
  },

  footer: {
    blurb:
      "Empowering educational institutions with a complete, 360-degree management solution. Your trusted partner in school digital transformation.",
    headingFeatures: "FEATURES",
    headingCompany: "COMPANY",
    headingContact: "CONTACT",
    /* The six feature rows take their labels from `features.groups` above,
       keyed by the same id the rail uses — see FOOTER_FEATURE_IDS in
       featureData.ts. Nothing about them is written here, so a group renamed
       for /features renames itself in the footer too. */
    allFeatures: "All {count}+ Features",
    companyAbout: "About Us",
    companyBlog: "Blog",
    companyPrivacy: "Privacy Policy",
    companyTerms: "Terms and Conditions",
    companyRefund: "Refund Policy",
    bookDemo: "Book a Free Demo",
    copyright: "© 2026 Kaympus • by Kayease. All rights reserved.",
    bottomSupport: "Support",
    bottomPrivacy: "Privacy",
    bottomTerms: "Terms",
    bottomRefund: "Refunds",
    /* Floating action buttons (bottom-right, every marketing page). */
    floatingWhatsApp: "Chat with us on WhatsApp",
    floatingWhatsAppMsg: "Hi! I would like to know more about Kaympus School Management ERP.",
    floatingSupport: "Request a Demo or Enquiry",
  },
};

export default en;
